import React from 'react';
import { useLingui } from '@lingui/react/macro';

import { Box, Flex, Text } from '@rover/kibble/core';
import { PetType } from '@rover/types/src/Pet';

import PetTypeIcon from './PetTypeIcon';
import SelectableOption from './SelectableOption';

type PetTypeOption = 'dog' | 'cat';

type Props = {
  onSelect: (petType: PetTypeOption) => void;
  dogCount: number;
  catCount: number;
  hideDog?: boolean;
  hideCat?: boolean;
};

const optionToPetType: Record<PetTypeOption, PetType> = {
  dog: PetType.DOG,
  cat: PetType.CAT,
};

const PetTypeSelector = ({
  onSelect,
  dogCount,
  catCount,
  hideDog,
  hideCat,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const options: Array<{ type: PetTypeOption; label: string; count: number }> = [
    ...(hideDog ? [] : [{ type: 'dog' as const, label: t`Dog`, count: dogCount }]),
    ...(hideCat ? [] : [{ type: 'cat' as const, label: t`Cat`, count: catCount }]),
  ];

  return (
    <Box>
      <Text size="200" fontWeight="semibold" mb="2x" display="block">
        {t`Your pet is a`}
      </Text>
      <Flex
        webAs="fieldset"
        role="radiogroup"
        gap="2x"
        sx={{ border: 'none', padding: 0, margin: 0 }}
      >
        {options.map(({ type, label, count }) => {
          const isSelected = count > 0;
          const displayLabel = count > 0 ? `${label} (${count})` : label;
          return (
            <SelectableOption
              key={type}
              isSelected={isSelected}
              onPress={() => onSelect(type)}
              label={displayLabel}
              icon={<PetTypeIcon petType={optionToPetType[type]} />}
            />
          );
        })}
      </Flex>
    </Box>
  );
};

export default PetTypeSelector;
